// Number related functions etc. required everywhere

/**
 * The Number.isInteger() method determines whether the passed value is an integer.  This is a polyfill for those browsers that don't support this method.
 *
 * @author GJ <gordon@zymewire.com>
 * Source: {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/isInteger|Mozilla Dev Network}
 *
 * @param value the value to check.
 * @returns {boolean} True if value is an integer.
 */
Number.isInteger = Number.isInteger || function(value) {
  return typeof value === 'number' &&
    isFinite(value) &&
    Math.floor(value) === value;
};

/**
 * The commifyNumber method takes a number (int or string) and returns a comma separated string grouped by thousands
 *
 * @author GJ <gordon@zymewire.com>
 *
 * @param value the value to commify.
 * @returns {string} a commified number, E.G. 1,000
 */
export function commifyNumber(num) {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/**
 * The makeMillions method takes a number (int only) and returns a comma separated string (grouped by thousands of millions) representing an abbreviation of the number in millions
 *
 * @example
 * // "$0 M"
 * makeMillions(1000);
 *
 * @example
 * // "$0.08 M"
 * makeMillions(85000);
 *
 * @example
 * // "$0.85 M"
 * makeMillions(850000); // 850 thousand
 *
 * @example
 * // "$85 M"
 * makeMillions(85000000); // 85 million
 *
 * @example
 * // "$8,500 M"
 * makeMillions(8500000000); // 8.5 billion
 * 
 * @author GJ <gordon@zymewire.com>
 *
 * @param amount {int} the value to work on.
 * @param nil_value {string} an optional value to return for nil values... the UI sometimes needs different presentations for nil
 * @returns {string} a comma formatted string standing for the original number reduced to it's representation in millions
 */
function makeMillions(amount, nil_value) {
  if (typeof nil_value === 'undefined'){
    nil_value = '---'
  }
  let formatted_amount = nil_value;
  if(Number.isInteger(amount)) {
    if (amount > 1) {
      let mil_form = Math.floor(amount/10000)/100
      let mil_form_commify = commifyNumber(mil_form)
      formatted_amount = '$' + mil_form_commify + ' M';
    }
    else {
      formatted_amount = '$0 M'
    }
  }
  return formatted_amount;
}

function makeMillionsSigned(amount) {
  if (!Number.isInteger(amount)) {
    return '---';
  }
  
  let amountMillions = Math.floor(amount/10000)/100
  let commifiedValue = commifyNumber(Math.abs(amountMillions))
  let sign = amountMillions < 0 ? "-" : ""
  return `${sign}${commifiedValue} M`
}

window.commifyNumber = commifyNumber;
window.makeMillions = makeMillions;
window.makeMillionsSigned = makeMillionsSigned;
